import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import PolicyStore from '../../../stores/Policy/PolicyStore';
import { Card, FTBody, Page, FuseTabs } from '../../Fuse';
import { POLICY_TESTING_CHANNELS } from '../../../stores/Policy/PolicyTesting/PolicyTestingStore';
import { TBody } from '../../Text/Text';
import FuseTextfield from '../../FuseControls/FuseTextfield';
import FuseButton from '../../FuseControls/FuseButton';
import { FuseConsoleCardFooter } from '../../ConsoleFragments';
import SnackStore from '../../../stores/SnackStore';
import FuseReadOnlyComponent from '../../FuseControls/FuseReadOnlyComponent';
import { REGISTRATION_TYPE } from '../../../stores/Policy/BrowsingRoles/BrowserIsolationOptionsStore';
import PolicyMigrationStore from '../../../stores/PolicyMigrationStore';
import { POLICY_MIGRATION_STEP_ENUM } from '../../../constants';
import ConsoleStore from '../../../stores/ConsoleStore';
import LegacyPolicyTesting from './LegacyPolicyTesting';
import './PolicyTesting.css';

const block = b.lock('PolicyTesting');

class PolicyTesting extends Component {
    static propTypes = {
        console: ConsoleStore.PropType.isRequired,
        policy: PolicyStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    fetchResults = () => {
        const { policy, snack } = this.props;

        policy.policyTesting.fetchResults()
            .catch(() => snack.showError('Failed to fetch results, please try again later'));
    };

    renderResultCard = (header, headerInfo, fields) => {
        return <Card
            className={block('ResultCard')}
            title={<Fragment>
                {header}
                {headerInfo ? <FTBody className={block('ResultHeaderInfo')}>({headerInfo})</FTBody> : null}
            </Fragment>}
        >
            {fields.map((field, index) => <FuseReadOnlyComponent
                key={index}
                readOnly
                readOnlyLabel={field.name}
                readOnlyValue={field.value}
            />)}
        </Card>;
    }

    getRegistrationTypeName = (value) => {
        if (value === REGISTRATION_TYPE.SAML_SSO) {
            return 'SAML/SSO';
        }
        if (value === REGISTRATION_TYPE.ACTIVATION_CODES) {
            return 'Activation Codes';
        }
        if (value === REGISTRATION_TYPE.ACTIVATION_LINKS) {
            return 'Activation Links';
        }
        return '';
    };

    isMigrationStepTwo = () => {
        const activeStep = PolicyMigrationStore.stepNumberFromString(this.props.console.policyMigrationStep);
        const newInTestStep = PolicyMigrationStore.stepNumberFromString(POLICY_MIGRATION_STEP_ENUM.NEW_IN_TEST);

        return activeStep === newInTestStep;
    }

    isOnTapTabButNoExclOrRedRules = (resultsRule) =>
        this.props.policy.policyTesting.activeTab.equals(POLICY_TESTING_CHANNELS.TAP_URL_ISOLATION)
            && !resultsRule?.redirectRule
            && !resultsRule?.exclusionRule

    renderResults = (results) => {
        const { role, rule, customCategories, standardCategories } = results;
        const { contentRule, redirectRule, exclusionRule } = rule || {};
        const activeTab = this.props.policy.policyTesting.activeTab;

        if (activeTab.equals(POLICY_TESTING_CHANNELS.CATEGORY_LOOKUP)) {
            return <div className={block('Results')}>
                {this.renderResultCard(
                    'Category Lookup',
                    '',
                    [
                        { name: 'Custom Categories',
                            value: customCategories.map((category) => category.name).join(', ') },
                        { name: 'Standard Categories',
                            value: standardCategories.map((category) => category.name).join(', ') },
                    ].filter((item) => item.value)
                )}
            </div>;
        }

        if (activeTab.equals(POLICY_TESTING_CHANNELS.BROWSER_EMAIL_REGISTRATION)) {
            return <div className={block('Results')}>
                {this.renderResultCard(
                    'Browser Registration',
                    'Browser/Email Isolation Only',
                    [
                        {
                            name: 'Registration Status',
                            value: role.browserRegistrationEnabled ? 'Enabled' : 'Disabled'
                        },
                        { name: 'Allow Anonymous Usage', value: role.anonymousUsage ? 'Enabled' : 'Disabled' },
                        {
                            name: 'Registration Type',
                            value: this.getRegistrationTypeName(role.browserAuthenticationMethod)
                        },
                        { name: 'Browsing Role', value: role.name },
                    ]
                )}
            </div>;
        }

        if (this.isOnTapTabButNoExclOrRedRules(results.rule)) {
            return <div className={block('Results')}>
                <Card className={block('ResultCard')} title={'No Policy Applied'}>
                    <TBody>
                        There are no matching Redirect Rules or Exclusion Rules. Therefore no Isolation Policy to apply.
                        The URL will open in the user’s native browser, subject to web gateway rules.
                    </TBody>
                </Card>
            </div>;
        }

        const product = activeTab.equals(POLICY_TESTING_CHANNELS.TAP_URL_ISOLATION)
            ? 'URL Isolation'
            : 'Browser/Email Isolation';

        return <div className={block('Results')}>
            {exclusionRule ? this.renderResultCard(
                'Applied Policy',
                '',
                [
                    { name: 'Action', value: 'Excluded from Isolation' },
                    { name: 'Exclusion Rule', value: exclusionRule.name },
                    this.createRoleOrGroupField(exclusionRule, role)
                ],
            ) : <Fragment>
                {contentRule ? this.renderResultCard(
                    'Applied Policy',
                    `For content requests in ${product}`,
                    [
                        {
                            name: 'Action',
                            value: contentRule.action === 'ALLOW'
                                ? 'Allow - Show the page contents'
                                : 'Block - Display a content blocked page',
                        },
                        { name: 'Content Rule', value: contentRule.name },
                        this.createRoleOrGroupField(contentRule, role)
                    ]
                ) : null}
                {redirectRule ? this.renderResultCard(
                    'Applied Policy',
                    `For redirects requests ${product}`,
                    [
                        { name: 'Action', value: 'Redirected to Isolation' },
                        { name: 'Redirect Rule', value: redirectRule.name },
                        this.createRoleOrGroupField(redirectRule, role)
                    ]
                ) : null}
            </Fragment>}
        </div>;
    };

    createRoleOrGroupField = (rule, role) => {
        if (rule.matchers && rule.matchers.groupNames && rule.matchers.groupNames.length > 0) {
            return { name: 'PPS Cloud User Sync Group', value: rule.matchers.groupNames[0] };
        }
        return { name: 'Browsing Role', value: role.name };
    }

    getFooterText = () => {
        const activeTab = this.props.policy.policyTesting.activeTab;

        if (activeTab.equals(POLICY_TESTING_CHANNELS.TAP_URL_ISOLATION)) {
            return 'Clicking ‘Show Results’ will display the applied Policy and matching components for ' +
                'two scenarios: redirects and content requests in URL Isolation.';
        }
        if (activeTab.equals(POLICY_TESTING_CHANNELS.BROWSER_EMAIL_ISOLATION)) {
            return 'Clicking ‘Show Results’ will display the applied Policy and matching components for the ' +
                'entered attributes.';
        }
        if (activeTab.equals(POLICY_TESTING_CHANNELS.CATEGORY_LOOKUP)) {
            return 'Clicking ‘Show Results’ will display all Categories associated with the URL entered.';
        }

        return 'Clicking ‘Show Results’ will display the assigned Browsing Role’s registration options.';
    };

    getNoResultsMessage = () =>
        (this.isMigrationStepTwo()
            ? <LegacyPolicyTesting />
            : <FTBody className={block('NoResults')}>No results found</FTBody>);

    getTabConfiguration = () => {
        const { policyTesting } = this.props.policy;
        const {
            activeTab,
            requestUrl,
            userEmail,
            senderEmail,
            results,
        } = policyTesting;
        const channel = activeTab.getValue();

        const configuration = {
            [POLICY_TESTING_CHANNELS.TAP_URL_ISOLATION]: {
                isRequestUrlVisible: true,
                isUserEmailVisible: true,
                isSenderEmailVisible: true,
                isValid: requestUrl.isValid && userEmail.isValid &&
                        (senderEmail.isEmpty || senderEmail.isValid),
                shouldShowResults: Boolean(results && results.role && results.rule)
                    && !(this.isOnTapTabButNoExclOrRedRules(results.rule) && this.isMigrationStepTwo())
            },
            [POLICY_TESTING_CHANNELS.BROWSER_EMAIL_ISOLATION]: {
                isRequestUrlVisible: true,
                isUserEmailVisible: true,
                isSenderEmailVisible: false,
                isValid: requestUrl.isValid && userEmail.isValid,
                shouldShowResults: Boolean(results && results.role && results.rule)
            },
            [POLICY_TESTING_CHANNELS.BROWSER_EMAIL_REGISTRATION]: {
                isRequestUrlVisible: false,
                isUserEmailVisible: true,
                isSenderEmailVisible: false,
                isValid: userEmail.isValid,
                shouldShowResults: Boolean(results && results.role)
            },
            [POLICY_TESTING_CHANNELS.CATEGORY_LOOKUP]: {
                isRequestUrlVisible: true,
                isUserEmailVisible: false,
                isSenderEmailVisible: false,
                isValid: requestUrl.isValid,
                shouldShowResults: Boolean(results && results.standardCategories?.length)
            },
        };
        return configuration[channel];
    }

    render() {
        const { policyTesting } = this.props.policy;
        const {
            activeTab,
            requestUrl,
            userEmail,
            senderEmail,
            results,
            policyEvaluateStatus,
            requestUrlError
        } = policyTesting;

        const { isRequestUrlVisible, isSenderEmailVisible, isUserEmailVisible, shouldShowResults, isValid }
            = this.getTabConfiguration();

        return <Page title="Policies - Policy Testing">
            <Card>
                <FuseTabs
                    containerClassName={block('PolicyChannelsTabs')}
                    store={activeTab}
                    onTabChange={() => policyTesting.onChangeTab()}
                    tabs={[{
                        label: 'TAP URL Isolation',
                        value: POLICY_TESTING_CHANNELS.TAP_URL_ISOLATION,
                        qa: 'tab-tap-isolation'
                    }, {
                        label: 'Browser/Email Isolation',
                        value: POLICY_TESTING_CHANNELS.BROWSER_EMAIL_ISOLATION,
                        qa: 'tab-browser-isolation'
                    }, {
                        label: 'Browser/Email Registration',
                        value: POLICY_TESTING_CHANNELS.BROWSER_EMAIL_REGISTRATION,
                        qa: 'tab-browser-registration'
                    }, {
                        label: 'Category Lookup',
                        value: POLICY_TESTING_CHANNELS.CATEGORY_LOOKUP,
                        qa: 'tab-category-lookup',
                    }]}
                />

                <TBody>Enter the request attributes.</TBody>

                <div className={block('InputFieldsContainer')}>
                    {isRequestUrlVisible ? <FuseTextfield
                        store={requestUrl}
                        className={block('InputField')}
                        label="URL"
                        placeholder={'http://example.com/'}
                        required
                        showRequired
                        error={!!requestUrlError}
                        bottomText={requestUrlError}
                    /> : null}

                    {isUserEmailVisible ? <FuseTextfield
                        store={userEmail}
                        className={block('InputField')}
                        label="User Email"
                        placeholder={'Enter User Email'}
                        required
                        showRequired
                    /> : null }

                    {isSenderEmailVisible ? <FuseTextfield
                        store={senderEmail}
                        className={block('InputField')}
                        label="Sender Email"
                        placeholder={'Enter Sender Email'}
                    /> : null}
                </div>

                <FuseButton
                    type={'primary'}
                    color={'blue'}
                    className={block('ShowResultsButton')}
                    loading={policyEvaluateStatus.isLoading}
                    onClick={() => this.fetchResults()}
                    disabled={!isValid}
                >
                    Show Results
                </FuseButton>

                <FuseConsoleCardFooter
                    className={block('InputsFooter')}
                    text={this.getFooterText()}
                />
            </Card>

            {results ? <Fragment>
                {shouldShowResults
                    ? this.renderResults(results)
                    : this.getNoResultsMessage()
                }
            </Fragment> : null}
        </Page>;
    }
}

export default inject('console', 'policy', 'snack')(observer(PolicyTesting));
